// src/pages/dashboard/AddVehicle.tsx
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { createCustomerVehicle, getCustomerVehicle, getVehicleFormOptions, updateCustomerVehicle } from '../../../services/customerVehicle.service';
import { Seo } from '../../components/Seo';
import { Car, Settings, Star, Camera, DollarSign, Phone, FileText, Eye, CheckCircle, ChevronRight, Upload, Plus, X, AlertCircle } from 'lucide-react';

interface CountryCallingCode {
  code: string;
  name: string;
  digits?: number;
  minDigits?: number;
  maxDigits?: number;
}

const COUNTRY_CODES: CountryCallingCode[] = [
  { code: '+212', name: 'Morocco', digits: 9 },
  { code: '+91', name: 'India', digits: 10 },
  { code: '+33', name: 'France', digits: 9 },
  { code: '+34', name: 'Spain', digits: 9 },
  { code: '+1', name: 'USA / Canada', digits: 10 },
  { code: '+44', name: 'UK', digits: 10 },
  { code: '+49', name: 'Germany', minDigits: 10, maxDigits: 11 },
  { code: '+39', name: 'Italy', minDigits: 9, maxDigits: 10 },
  { code: '+971', name: 'UAE', digits: 9 },
  { code: '+966', name: 'Saudi Arabia', digits: 9 },
];

const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);
const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const MAX_IMAGES = 10;

// STEP DEFINITIONS (now translated via t() in the component)
const STEPS = [
  { key: 'vehicleInfo', icon: Car },
  { key: 'technical', icon: Settings },
  { key: 'features', icon: Star },
  { key: 'photos', icon: Camera },
  { key: 'pricing', icon: DollarSign },
  { key: 'contact', icon: Phone },
  { key: 'description', icon: FileText },
  { key: 'preview', icon: Eye },
  { key: 'published', icon: CheckCircle },
];

// Feature list keys (translated)
const FEATURE_KEYS = [
  'leatherSeats', 'heatedSeats', 'ventilatedSeats', 'memorySeats',
  'panoramicSunroof', 'sunroof', 'navigationGPS', 'appleCarPlay',
  'androidAuto', 'bluetooth', 'usbPorts', 'wirelessCharging',
  'reverseCamera', 'camera360', 'parkingSensors', 'parkingAssist',
  'adaptiveCruiseControl', 'laneDepartureWarning', 'blindSpotMonitor',
  'automaticEmergencyBraking', 'ledHeadlights', 'keylessEntry',
  'pushStart', 'remoteStart', 'climateControl', 'dualZoneClimate',
  'alloyWheels', 'tintedWindows', 'electricTrunk', 'headUpDisplay',
];

export function AddVehicle() {
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const { id: vehicleId } = useParams<{ id: string }>();
  const isEditMode = Boolean(vehicleId);
  const [formOptions, setFormOptions] = useState<any>({});
  const [formError, setFormError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [countryCode, setCountryCode] = useState('+212');
  const [localPhoneNumber, setLocalPhoneNumber] = useState('');
  
  // ----- Single-language fields (for non-text, or fallback) -----
  const [formData, setFormData] = useState<Record<string, string>>({});
  
  // ----- Multilingual fields (objects with language keys) -----
  const [titleML, setTitleML] = useState<Record<string, string>>({});
  const [descriptionML, setDescriptionML] = useState<Record<string, string>>({});
  const [categoryML, setCategoryML] = useState<Record<string, string>>({});
  const [conditionML, setConditionML] = useState<Record<string, string>>({});
  const [serviceHistoryML, setServiceHistoryML] = useState<Record<string, string>>({});
  
  // ----- Language toggle for description step -----
  const [formLang, setFormLang] = useState<string>('en'); // 'en' or 'de'
  
  // ----- Other states -----
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [existingImagePaths, setExistingImagePaths] = useState<string[]>([]);
  const inputContext = useRef<any>(null);
  const [step, setStep] = useState(0);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [savedVehicleId, setSavedVehicleId] = useState(vehicleId || '');
  
  const isDealer = Number(Cookies.get('customerType')) === 3;
  const listingsPath = isDealer ? '/dealer/inventory' : '/dashboard/listings';
  const addVehiclePath = isDealer ? '/dealer/add-vehicle' : '/dashboard/add-vehicle';

  // ----- Load vehicle data for edit mode -----
  useEffect(() => {
    const load = async () => {
      try {
        setFormError('');
        const options = await getVehicleFormOptions();
        setFormOptions(options);
        if (!vehicleId) return;
        
        const vehicle = await getCustomerVehicle(vehicleId);
        const currentImages = Array.isArray(vehicle.Images) ? vehicle.Images : [];
        setExistingImagePaths(currentImages);
        const apiBaseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(/\/api\/v1\/?$/, '');
        setPhotos(currentImages.map((image: string) => image.startsWith('http') ? image : `${apiBaseUrl}/volume/${image.replace(/^\/+/, '')}`));
        
        const brandObj = (options.brands || []).find((b: any) => b.id === vehicle.BrandID);
        const derivedMakeId = vehicle.MakeID || brandObj?.MakeID || '';

        const storedPhoneNumber = String(vehicle.PhoneNumber || '').trim();
        const matchedCountry = COUNTRY_CODES.find(country => storedPhoneNumber.startsWith(country.code));
        const parsedCountryCode = matchedCountry?.code || '+212';
        const parsedLocalPhone = (matchedCountry
          ? storedPhoneNumber.slice(matchedCountry.code.length)
          : storedPhoneNumber
        ).replace(/\D/g, '');
        setCountryCode(parsedCountryCode);
        setLocalPhoneNumber(parsedLocalPhone);

        // Populate single-language fields
        setFormData({
          MakeID: derivedMakeId,
          Category: vehicle.Category || '',
          BrandID: vehicle.BrandID || '',
          ModelID: vehicle.ModelID || '',
          BodyTypeID: vehicle.BodyTypeID || '',
          ColorID: vehicle.ColorID || '',
          FuelTypeID: vehicle.FuelTypeID || '',
          CountryID: vehicle.CountryID || '',
          StateID: vehicle.StateID || '',
          City: vehicle.City || '',
          YearOfRegistration: vehicle.YearOfRegistration || '',
          Mileage: vehicle.Mileage || '',
          Price: vehicle.Price || '',
          Title: vehicle.Title || '',
          VehicleDescription: vehicle.VehicleDescription || '',
          TransmissionType: String(vehicle.TransmissionType || 1),
          EngineDisplacement: vehicle.EngineDisplacement || '',
          EngineHorsePower: vehicle.EngineHorsePower || '',
          DriveType: vehicle.DriveType || '',
          NumberOfDoors: String(vehicle.NumberOfDoors || ''),
          NumberOfSeats: vehicle.NumberOfSeats || '',
          VIN: vehicle.VIN || '',
          Condition: vehicle.Condition || '',
          ServiceHistory: vehicle.ServiceHistory || '',
          IsNegotiable: String(Boolean(vehicle.IsNegotiable)),
          IsExchangeable: String(Boolean(vehicle.IsExchangeable)),
          FinancingAvailable: vehicle.FinancingAvailable || 'No',
          ShowPhone: String(vehicle.ShowPhone !== false),
          ShowWhatsApp: String(vehicle.ShowWhatsApp !== false),
          ShowMessages: String(vehicle.ShowMessages !== false),
          PhoneNumber: storedPhoneNumber,
          ContactHoursStart: vehicle.ContactHoursStart || '09:00',
          ContactHoursEnd: vehicle.ContactHoursEnd || '18:00',
        });
        
        // Populate ML fields
        const rawTitleML = vehicle.TitleML && Object.keys(vehicle.TitleML).length > 0
          ? vehicle.TitleML
          : (vehicle.Title ? { en: vehicle.Title, de: vehicle.Title } : {});
        const rawDescML = vehicle.VehicleDescriptionML && Object.keys(vehicle.VehicleDescriptionML).length > 0
          ? vehicle.VehicleDescriptionML
          : (vehicle.VehicleDescription ? { en: vehicle.VehicleDescription, de: vehicle.VehicleDescription } : {});

        setTitleML(rawTitleML);
        setDescriptionML(rawDescML);
        setCategoryML(vehicle.CategoryML || {});
        setConditionML(vehicle.ConditionML || {});
        setServiceHistoryML(vehicle.ServiceHistoryML || {});
        
        // Set current language to first available or 'en'
        const langs = Object.keys(rawTitleML);
        if (langs.length > 0) setFormLang(langs[0]);
        
        // Features
        const rawFeatures = Array.isArray(vehicle.FeatureIDs) ? vehicle.FeatureIDs : [];
        setSelectedFeatures(rawFeatures);
        
      } catch (error: any) {
        setFormError(error.response?.data?.message || t('addVehicle.errors.loadFailed'));
      }
    };
    void load();
  }, [vehicleId, t]);

  // ----- Field mapping helpers -----
  const fieldKeys: Record<string, string> = {
    Make: 'MakeID', Brand: 'BrandID', Model: 'ModelID', 'Body Type': 'BodyTypeID',
    Color: 'ColorID', 'Fuel Type': 'FuelTypeID', Country: 'CountryID', State: 'StateID',
    City: 'City', Year: 'YearOfRegistration', 'Mileage (km)': 'Mileage',
    'Asking Price (MAD)': 'Price', 'Title / Headline': 'Title',
    Description: 'VehicleDescription', Transmission: 'TransmissionType',
    'Engine Displacement (cc)': 'EngineDisplacement', 'Power (HP)': 'EngineHorsePower',
    'Drive Type': 'DriveType', 'Number of Doors': 'NumberOfDoors',
    'Number of Seats': 'NumberOfSeats', 'VIN (optional)': 'VIN',
    Condition: 'Condition', 'Public Phone Number': 'PhoneNumber'
  };
  
  const saveValue = (label: string, value: string) => {
    const key = fieldKeys[label] || label;
    setFormData(old => {
      const updated = { ...old, [key]: value };
      if (key === 'MakeID') {
        updated.BrandID = '';
        updated.ModelID = '';
      } else if (key === 'BrandID') {
        updated.ModelID = '';
      } else if (key === 'CountryID') {
        updated.StateID = '';
        updated.City = '';
      } else if (key === 'StateID') {
        updated.City = '';
      }
      return updated;
    });
    setErrors(prev => ({ ...prev, [key]: '' }));
  };

  // ----- Dynamic options for selects -----
  const dynamicOptions = (label: string) => {
    const data = formOptions || {};
    if (label === 'Make') return (data.makes || []).map((x: any) => ({ value: x.id, label: x.MakeName }));
    if (label === 'Brand') return (data.brands || []).filter((x: any) => !formData.MakeID || x.MakeID === formData.MakeID).map((x: any) => ({ value: x.id, label: x.BrandName }));
    if (label === 'Model') return (data.models || []).filter((x: any) => !formData.BrandID || x.BrandID === formData.BrandID).map((x: any) => ({ value: x.id, label: x.ModelName }));
    if (label === 'Body Type') return (data.bodyTypes || []).map((x: any) => ({ value: x.id, label: x.BodyTypeName }));
    if (label === 'Color') return (data.colors || []).map((x: any) => ({ value: x.id, label: x.ColorName }));
    if (label === 'Fuel Type') return (data.fuelTypes || []).map((x: any) => ({ value: x.id, label: x.FuelTypeName }));
    if (label === 'Country') return (data.countries || []).map((x: any) => ({ value: x.id, label: x.CountryName }));
    if (label === 'State') return (data.states || []).filter((x: any) => !formData.CountryID || x.CountryID === formData.CountryID).map((x: any) => ({ value: x.id, label: x.StateName }));
    if (label === 'City') return (data.cities || []).filter((x: any) => !formData.StateID || x.StateID === formData.StateID).map((x: any) => ({ value: x.CityName || x.id, label: x.CityName }));
    if (label === 'Transmission') return [
      { value: '1', label: t('addVehicle.step1.transmissionOptions.automatic') },
      { value: '2', label: t('addVehicle.step1.transmissionOptions.manual') },
      { value: '3', label: t('addVehicle.step1.transmissionOptions.cvt') },
      { value: '4', label: t('addVehicle.step1.transmissionOptions.semiAutomatic') }
    ];
    return null;
  };

  inputContext.current = { formData, fieldKeys, saveValue, errors, dynamicOptions, t };

  const selectedOptionName = (items: any[], id: string | undefined, nameKey: string) =>
    items?.find((item: any) => item.id === id)?.[nameKey] || '—';

  // ----- Step inline validation -----
  const validateCurrentStep = (stepNumber: number): boolean => {
    const newErrors: Record<string, string> = {};
    if (stepNumber === 0) {
      if (!formData.MakeID?.trim()) newErrors.MakeID = 'Please select a Make';
      if (!formData.BrandID?.trim()) newErrors.BrandID = 'Please select a Brand';
      if (!formData.ModelID?.trim()) newErrors.ModelID = 'Please select a Model';
      if (!formData.YearOfRegistration?.trim()) newErrors.YearOfRegistration = 'Registration Year is required';
      if (!formData.BodyTypeID?.trim()) newErrors.BodyTypeID = 'Please select a Body Type';
      if (!formData.ColorID?.trim()) newErrors.ColorID = 'Please select a Color';
      if (!formData.CountryID?.trim()) newErrors.CountryID = 'Please select a Country';
      if (!formData.StateID?.trim()) newErrors.StateID = 'Please select a State';
      if (!formData.City?.trim()) newErrors.City = 'Please select a City';
    } else if (stepNumber === 1) {
      if (!formData.Mileage?.trim()) newErrors.Mileage = 'Mileage is required';
      if (!formData.FuelTypeID?.trim()) newErrors.FuelTypeID = 'Please select a Fuel Type';
      if (!formData.TransmissionType?.trim()) newErrors.TransmissionType = 'Please select a Transmission';
      if (!formData.Condition?.trim()) newErrors.Condition = 'Please select Vehicle Condition';
    } else if (stepNumber === 2) {
      if (selectedFeatures.length === 0) {
        newErrors.FeatureIDs = 'Please select at least one vehicle feature.';
      }
    } else if (stepNumber === 3) {
      if (existingImagePaths.length + photoFiles.length === 0) {
        newErrors.Images = 'Please upload at least one vehicle image.';
      }
    } else if (stepNumber === 4) {
      if (!formData.Price?.trim() || parseFloat(formData.Price) <= 0) {
        newErrors.Price = 'Please enter a valid price';
      }
    } else if (stepNumber === 5) {
      const selectedCountry = COUNTRY_CODES.find(country => country.code === countryCode);
      if (formData.ShowPhone !== 'false' && !localPhoneNumber) {
        newErrors.PhoneNumber = 'Phone number is required when phone contact is enabled';
      } else if (localPhoneNumber && selectedCountry?.digits && localPhoneNumber.length !== selectedCountry.digits) {
        newErrors.PhoneNumber = `Phone number for ${selectedCountry.name} must be ${selectedCountry.digits} digits`;
      } else if (
        localPhoneNumber &&
        selectedCountry?.minDigits &&
        selectedCountry?.maxDigits &&
        (localPhoneNumber.length < selectedCountry.minDigits || localPhoneNumber.length > selectedCountry.maxDigits)
      ) {
        newErrors.PhoneNumber = `Phone number for ${selectedCountry.name} must be between ${selectedCountry.minDigits} and ${selectedCountry.maxDigits} digits`;
      }
            const startTime = formData.ContactHoursStart || '09:00';
      const endTime = formData.ContactHoursEnd || '18:00';

      if (startTime && endTime) {
        const startMinutes =
          Number(startTime.split(':')[0]) * 60 +
          Number(startTime.split(':')[1]);

        const endMinutes =
          Number(endTime.split(':')[0]) * 60 +
          Number(endTime.split(':')[1]);

        if (endMinutes < startMinutes) {
          newErrors.ContactHours =
            'End Time cannot be earlier than Start Time.';
        }
      }
    } else if (stepNumber === 6) {
      if (!formData.Title?.trim()) newErrors.Title = 'Title / Headline is required';
      if (!formData.VehicleDescription?.trim()) newErrors.VehicleDescription = 'Vehicle Description is required';
    }

    setErrors(prev => ({
      ...prev,
      ...(stepNumber === 3 ? { Images: '' } : {}),
      ...newErrors,
    }));
    if (Object.keys(newErrors).length > 0) {
      setFormError('Please fill in all required fields highlighted in red.');
      return false;
    }
    setFormError('');
    return true;
  };

  // ----- Publish / Save -----
  const publish = async () => {
    try {
      setUploading(true);
      setFormError('');
      const payload = new FormData();
      
      const requiredFields: Record<string, string> = {
        MakeID: 'Make',
        BrandID: 'Brand',
        ModelID: 'Model',
        BodyTypeID: 'Body Type',
        ColorID: 'Color',
        YearOfRegistration: 'Year',
        CountryID: 'Country',
        StateID: 'State',
        City: 'City',
        Mileage: 'Mileage (km)',
        Price: 'Asking Price (MAD)'
      };
      const missing = Object.entries(requiredFields)
        .filter(([key]) => !formData[key]?.trim())
        .map(([, label]) => label);
      if (missing.length) {
        setFormError(t('addVehicle.errors.missingFields', { fields: missing.join(', ') }));
        return;
      }
      if (selectedFeatures.length === 0) {
        setErrors(previous => ({
          ...previous,
          FeatureIDs: 'Please select at least one vehicle feature.',
        }));
        setFormError('Please select at least one vehicle feature.');
        setStep(2);
        return;
      }
      
      // Append single-language fields
      Object.entries(formData).forEach(([key, value]) => {
        if (value && value.trim()) payload.append(key, value);
      });
      
      // Ensure Title and VehicleDescription are included
      const titleValue = formData.Title || titleML[formLang] || titleML['en'] || Object.values(titleML)[0] || '';
      if (titleValue && !formData.Title) payload.append('Title', titleValue);
      const descValue = formData.VehicleDescription || descriptionML[formLang] || descriptionML['en'] || Object.values(descriptionML)[0] || '';
      if (descValue && !formData.VehicleDescription) payload.append('VehicleDescription', descValue);
      
      // Append ML fields as JSON strings
      payload.append('TitleML', JSON.stringify(titleML));
      payload.append('VehicleDescriptionML', JSON.stringify(descriptionML));
      payload.append('CategoryML', JSON.stringify(categoryML));
      payload.append('ConditionML', JSON.stringify(conditionML));
      payload.append('ServiceHistoryML', JSON.stringify(serviceHistoryML));
      
      // Features (save UUIDs)
      payload.append('FeatureIDs', JSON.stringify(selectedFeatures));
      
      // FuelType
      const fuel = (formOptions.fuelTypes || []).find((x:any) => x.id === formData.FuelTypeID);
      if (fuel) payload.append('FuelType', fuel.FuelTypeName);
      
      // Images
      photoFiles.forEach(file => payload.append('Images', file));
      
      if (isEditMode) {
        payload.append('ExistingImages', JSON.stringify(existingImagePaths));
        const result = await updateCustomerVehicle(vehicleId!, payload);
        setSavedVehicleId(result.data?.id || vehicleId!);
      } else {
        const result = await createCustomerVehicle(payload);
        setSavedVehicleId(result.data?.id || '');
      }
      setStep(8);
    } catch (error: any) {
      setFormError(error.response?.data?.message || t(`addVehicle.errors.${isEditMode ? 'saveFailed' : 'publishFailed'}`));
    } finally {
      setUploading(false);
    }
  };

  const next = () => {
    if (!validateCurrentStep(step)) return;
    if (step === 7) void publish();
    else setStep(s => Math.min(s + 1, 8));
  };
  const prev = () => {
    setFormError('');
    setStep(s => Math.max(s - 1, 0));
  };

  // ----- Features toggle -----
  const toggleFeature = (id: string) => {
    setSelectedFeatures(sf => sf.includes(id) ? sf.filter(x => x !== id) : [...sf, id]);
    setErrors(previous => ({ ...previous, FeatureIDs: '' }));
    setFormError('');
  };

  // ----- Photos -----
  const addPhotos = (files: FileList | null) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;
    setPhotoFiles(currentFiles => {
      const validationErrors: string[] = [];
      const knownFiles = new Set(
        currentFiles.map(file => `${file.name}:${file.size}:${file.lastModified}`),
      );
      const validFiles = selected.filter(file => {
        const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
        if (!ALLOWED_IMAGE_TYPES.has(file.type) || !ALLOWED_IMAGE_EXTENSIONS.includes(extension)) {
          validationErrors.push(`${file.name}: only JPG, JPEG, PNG, and WebP images are allowed.`);
          return false;
        }
        if (file.size > MAX_IMAGE_SIZE) {
          validationErrors.push(`${file.name}: image must be 5 MB or smaller.`);
          return false;
        }
        const signature = `${file.name}:${file.size}:${file.lastModified}`;
        if (knownFiles.has(signature)) {
          validationErrors.push(`${file.name}: this image is already selected.`);
          return false;
        }
        knownFiles.add(signature);
        return true;
      });

      const remaining = Math.max(0, MAX_IMAGES - existingImagePaths.length - currentFiles.length);
      const additions = validFiles.slice(0, remaining);
      if (validFiles.length > remaining) {
        validationErrors.push(`You can upload a maximum of ${MAX_IMAGES} images.`);
      }
      setErrors(previous => ({ ...previous, Images: validationErrors.join(' ') }));
      setPhotos(currentPhotos => [...currentPhotos, ...additions.map(file => URL.createObjectURL(file))]);
      return [...currentFiles, ...additions];
    });
  };

  const removePhoto = (index: number) => {
    setPhotos(currentPhotos => {
      URL.revokeObjectURL(currentPhotos[index]);
      return currentPhotos.filter((_, currentIndex) => currentIndex !== index);
    });
    if (index < existingImagePaths.length) {
      setExistingImagePaths(currentImages => currentImages.filter((_, currentIndex) => currentIndex !== index));
    } else {
      setPhotoFiles(currentFiles => currentFiles.filter((_, currentIndex) => currentIndex !== index - existingImagePaths.length));
    }
  };

  // ----- Memoized Input component (uses context) -----
  const Input = useMemo(() => ({ label, placeholder, type = 'text', required = false, hint }: any) => {
    const { formData, fieldKeys, saveValue, errors } = inputContext.current;
    const key = fieldKeys[label] || label;
    const value = formData[key] || '';
    const error = errors[key];
    return (
      <div>
        <label className="block text-sm text-[#2D323A] mb-1.5 font-medium">
          {label} {required && <span className="text-[#EF4444]">*</span>}
          {hint && <span className="text-xs text-[#717182] font-normal ml-1">{hint}</span>}
        </label>
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={e => saveValue(label, e.target.value)}
          className={`w-full px-4 py-3 bg-[#F5F7FA] border rounded-xl focus:outline-none transition-colors ${
            error ? 'border-[#EF4444] focus:border-[#EF4444]' : 'border-[#E5E7EB] focus:border-[#1E6DFF]'
          }`}
        />
        {error && <p className="text-xs text-[#EF4444] mt-1">{error}</p>}
      </div>
    );
  }, []);

  const Select = useMemo(() => ({ label, options = [], required = false, hint }: any) => {
    const { formData, fieldKeys, saveValue, errors, dynamicOptions, t } = inputContext.current;
    const dynamic = dynamicOptions(label);
    const key = fieldKeys[label] || label;
    const value = formData[key] || '';
    const error = errors[key];
    const isDisabled = 
      (label === 'Brand' && !formData.MakeID) ||
      (label === 'Model' && !formData.BrandID) ||
      (label === 'State' && !formData.CountryID) ||
      (label === 'City' && !formData.StateID);

    return (
      <div>
        <label className="block text-sm text-[#2D323A] mb-1.5 font-medium">
          {label} {required && <span className="text-[#EF4444]">*</span>}
          {hint && <span className="text-xs text-[#717182] font-normal ml-1">{hint}</span>}
        </label>
        <select
          value={value}
          disabled={isDisabled}
          onChange={e => saveValue(label, e.target.value)}
          className={`w-full px-4 py-3 bg-[#F5F7FA] border rounded-xl focus:outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
            error ? 'border-[#EF4444] focus:border-[#EF4444]' : 'border-[#E5E7EB] focus:border-[#1E6DFF]'
          }`}
        >
          <option value="">
            {isDisabled
              ? (label === 'Brand' ? 'Select Make first' : label === 'Model' ? 'Select Brand first' : label === 'State' ? 'Select Country first' : 'Select State first')
              : t('addVehicle.buttons.select')}
          </option>
          {(dynamic || (options || []).map((o: any) => ({ value: o, label: o }))).map((o: any) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {error && <p className="text-xs text-[#EF4444] mt-1">{error}</p>}
      </div>
    );
  }, []);

  // ----- Get translated steps -----
  const steps = STEPS.map(s => ({
    ...s,
    label: t(`addVehicle.steps.${s.key}`)
  }));



  return (
    <div className="p-4 md:p-8">
      <Seo
        title={isEditMode ? t('addVehicle.seo.edit') : t('addVehicle.seo.add')}
        description={isEditMode ? t('addVehicle.seo.editDesc') : t('addVehicle.seo.addDesc')}
      />
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl text-[#2D323A] mb-1">
            {isEditMode ? t('addVehicle.title.edit') : t('addVehicle.title.add')}
          </h1>
          <p className="text-[#717182]">
            {t('addVehicle.subtitle', { current: step + 1, total: steps.length, step: steps[step]?.label || '' })}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="flex items-center mb-8 overflow-x-auto pb-2 gap-0">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="flex items-center flex-1 last:flex-none flex-shrink-0">
                <button
                  onClick={() => i <= step && setStep(i)}
                  className={`flex flex-col items-center ${i <= step ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all text-xs ${
                    i < step ? 'bg-[#22C55E] text-white' :
                    i === step ? 'bg-[#1E6DFF] text-white shadow-md' :
                    'bg-[#E5E7EB] text-[#717182]'
                  }`}>
                    {i < step ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <span className={`text-xs mt-1 hidden lg:block whitespace-nowrap ${i === step ? 'text-[#1E6DFF]' : 'text-[#717182]'}`}>
                    {s.label}
                  </span>
                </button>
                {i < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 min-w-[12px] transition-colors ${i < step ? 'bg-[#22C55E]' : 'bg-[#E5E7EB]'}`} />
                )}
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          {/* Step 0: Vehicle Info */}
          {step === 0 && (
            <div>
              <h2 className="text-xl text-[#2D323A] mb-5">{t('addVehicle.step0.title')}</h2>
              <div className="grid grid-cols-2 gap-4">
                <Select label="Make" required options={[]} />
                <Select label="Brand" required options={[]} />
                <Select label="Model" required options={[]} />
                <Input label="Year" required placeholder={t('addVehicle.step0.yearPlaceholder')} type="number" />
                <Select label="Body Type" required options={[
                  t('addVehicle.step0.bodyTypeOptions.sedan'),
                  t('addVehicle.step0.bodyTypeOptions.suv'),
                  t('addVehicle.step0.bodyTypeOptions.hatchback'),
                  t('addVehicle.step0.bodyTypeOptions.coupe'),
                  t('addVehicle.step0.bodyTypeOptions.convertible'),
                  t('addVehicle.step0.bodyTypeOptions.wagon'),
                  t('addVehicle.step0.bodyTypeOptions.minivan'),
                  t('addVehicle.step0.bodyTypeOptions.pickup')
                ]} />
                <Select label="Color" required options={[
                  t('addVehicle.step0.colorOptions.black'),
                  t('addVehicle.step0.colorOptions.white'),
                  t('addVehicle.step0.colorOptions.silver'),
                  t('addVehicle.step0.colorOptions.gray'),
                  t('addVehicle.step0.colorOptions.blue'),
                  t('addVehicle.step0.colorOptions.red'),
                  t('addVehicle.step0.colorOptions.other')
                ]} />
                <Select label="Country" required options={[]} />
                <Select label="State" required options={[]} />
                <Select label="City" required options={[]} />
              </div>
            </div>
          )}

          {/* Step 1: Technical Details */}
          {step === 1 && (
            <div>
              <h2 className="text-xl text-[#2D323A] mb-5">{t('addVehicle.step1.title')}</h2>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Mileage (km)" required placeholder={t('addVehicle.step1.mileagePlaceholder')} type="number" />
                <Select label="Fuel Type" required options={[
                  t('addVehicle.step1.fuelTypeOptions.petrol'),
                  t('addVehicle.step1.fuelTypeOptions.diesel'),
                  t('addVehicle.step1.fuelTypeOptions.hybrid'),
                  t('addVehicle.step1.fuelTypeOptions.electric'),
                  t('addVehicle.step1.fuelTypeOptions.lpg')
                ]} />
                <Select label="Transmission" required options={[
                  t('addVehicle.step1.transmissionOptions.automatic'),
                  t('addVehicle.step1.transmissionOptions.manual'),
                  t('addVehicle.step1.transmissionOptions.cvt'),
                  t('addVehicle.step1.transmissionOptions.semiAutomatic')
                ]} />
                <Select label="Condition" required options={[
                  t('addVehicle.step1.conditionOptions.excellent'),
                  t('addVehicle.step1.conditionOptions.veryGood'),
                  t('addVehicle.step1.conditionOptions.good'),
                  t('addVehicle.step1.conditionOptions.fair'),
                  t('addVehicle.step1.conditionOptions.partsOnly')
                ]} />
                <Input label="Engine Displacement (cc)" hint="(optional)" placeholder={t('addVehicle.step1.enginePlaceholder')} type="number" />
                <Input label="Power (HP)" hint="(optional)" placeholder={t('addVehicle.step1.powerPlaceholder')} type="number" />
                <Select label="Drive Type" hint="(optional)" options={[
                  t('addVehicle.step1.driveTypeOptions.fwd'),
                  t('addVehicle.step1.driveTypeOptions.rwd'),
                  t('addVehicle.step1.driveTypeOptions.awd'),
                  t('addVehicle.step1.driveTypeOptions.fourWd')
                ]} />
                <Select label="Number of Doors" hint="(optional for bike/ATV)" options={[
                  t('addVehicle.step1.doorsOptions.2'),
                  t('addVehicle.step1.doorsOptions.3'),
                  t('addVehicle.step1.doorsOptions.4'),
                  t('addVehicle.step1.doorsOptions.5')
                ]} />
                <Select label="Number of Seats" hint="(optional for bike/ATV)" options={[
                  t('addVehicle.step1.seatsOptions.2'),
                  t('addVehicle.step1.seatsOptions.4'),
                  t('addVehicle.step1.seatsOptions.5'),
                  t('addVehicle.step1.seatsOptions.7'),
                  t('addVehicle.step1.seatsOptions.8plus')
                ]} />
                <Input label="VIN (optional)" hint="(optional)" placeholder={t('addVehicle.step1.vinPlaceholder')} />
              </div>
            </div>
          )}

          {/* Step 2: Features */}
          {step === 2 && (
            <div>
              <h2 className="text-xl text-[#2D323A] mb-2">{t('addVehicle.step2.title')}</h2>
              <p className="text-[#717182] text-sm mb-5">
                {t('addVehicle.step2.subtitle', { count: selectedFeatures.length })}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {(formOptions.features || []).map((feat: any) => {
                  const isChecked = selectedFeatures.includes(feat.id);
                  return (
                    <label key={feat.id} className={`flex items-center gap-2 p-3 border rounded-xl cursor-pointer transition-all ${
                      isChecked
                        ? 'border-[#1E6DFF] bg-[#1E6DFF]/5 text-[#1E6DFF]'
                        : 'border-[#E5E7EB] text-[#2D323A] hover:border-[#BFC2C7]'
                    }`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleFeature(feat.id)}
                        className="accent-[#1E6DFF] w-4 h-4"
                      />
                      <span className="text-sm">{feat.FeatureName}</span>
                    </label>
                  );
                })}
              </div>
              {errors.FeatureIDs && (
                <p className="mt-3 text-sm text-[#EF4444]" role="alert">
                  {errors.FeatureIDs}
                </p>
              )}
            </div>
          )}

          {/* Step 3: Photos */}
          {step === 3 && (
            <div>
              <h2 className="text-xl text-[#2D323A] mb-2">{t('addVehicle.step3.title')}</h2>
              <p className="text-[#717182] text-sm mb-5">{t('addVehicle.step3.subtitle')}</p>
              <input
                id="vehicle-images"
                type="file"
                accept="image/*"
                multiple
                onChange={event => { addPhotos(event.target.files); event.target.value = ''; }}
                className="mb-4 block w-full text-sm"
              />
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-4">
                {photos.map((p, i) => (
                  <div key={p} className="relative aspect-square bg-[#F5F7FA] rounded-xl overflow-hidden border border-[#E5E7EB]">
                    <img src={p} alt={`Vehicle photo ${i + 1}`} className="h-full w-full object-cover" />
                    {i === 0 && <span className="absolute top-1 left-1 text-xs px-1.5 py-0.5 bg-[#1E6DFF] text-white rounded-lg">{t('addVehicle.step3.main')}</span>}
                    <button
                      type="button"
                      aria-label={t('addVehicle.step3.removePhoto')}
                      onClick={() => removePhoto(i)}
                      className="absolute top-1 right-1 p-1 bg-white/80 rounded-lg text-[#EF4444] hover:bg-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => document.getElementById('vehicle-images')?.click()}
                  className="aspect-square border-2 border-dashed border-[#E5E7EB] rounded-xl flex flex-col items-center justify-center hover:border-[#1E6DFF] hover:bg-[#1E6DFF]/5 transition-colors text-[#717182] hover:text-[#1E6DFF]"
                >
                  <Plus className="w-7 h-7 mb-1" />
                  <span className="text-xs">{t('addVehicle.step3.addPhoto')}</span>
                </button>
              </div>
              {errors.Images && <p className="text-xs text-[#EF4444] mb-4">{errors.Images}</p>}
              {uploading && (
                <div className="bg-[#F5F7FA] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2 text-sm">
                    <span className="text-[#717182]">{t('addVehicle.step3.uploading')}</span>
                    <span className="text-[#1E6DFF]">{t('addVehicle.step3.uploadProgress', { progress: uploadProgress })}</span>
                  </div>
                  <div className="h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                    <div className="h-full bg-[#1E6DFF] rounded-full transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
                  </div>
                </div>
              )}
              <div className="bg-[#F5F7FA] rounded-xl p-3 mt-3">
                <p className="text-xs text-[#717182]">{t('addVehicle.step3.tips')}</p>
              </div>
            </div>
          )}

          {/* Step 4: Pricing */}
          {step === 4 && (
            <div>
              <h2 className="text-xl text-[#2D323A] mb-5">{t('addVehicle.step4.title')}</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[#2D323A] mb-1.5 font-medium">
                    {t('addVehicle.step4.askingPrice')} <span className="text-[#EF4444]">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      placeholder={t('addVehicle.step4.pricePlaceholder')}
                      value={formData.Price || ''}
                      onChange={event => {
                        saveValue('Asking Price (MAD)', event.target.value);
                        setErrors(prev => ({ ...prev, Price: '' }));
                      }}
                      className={`w-full pl-4 pr-20 py-3 bg-[#F5F7FA] border rounded-xl focus:outline-none text-xl transition-colors ${
                        errors.Price ? 'border-[#EF4444] focus:border-[#EF4444]' : 'border-[#E5E7EB] focus:border-[#1E6DFF]'
                      }`}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#717182]">MAD</span>
                  </div>
                  {errors.Price && <p className="text-xs text-[#EF4444] mt-1">{errors.Price}</p>}
                </div>
                <div className="flex items-center gap-3 p-3 bg-[#F5F7FA] rounded-xl">
                  <input
                    type="checkbox"
                    id="nego"
                    checked={formData.IsNegotiable === 'true'}
                    onChange={event => saveValue('IsNegotiable', String(event.target.checked))}
                    className="accent-[#1E6DFF] w-4 h-4"
                  />
                  <label htmlFor="nego" className="text-sm text-[#2D323A]">{t('addVehicle.step4.negotiable')}</label>
                </div>
                <div className="flex items-center gap-3 p-3 bg-[#F5F7FA] rounded-xl">
                  <input
                    type="checkbox"
                    id="exchange"
                    checked={formData.IsExchangeable === 'true'}
                    onChange={event => saveValue('IsExchangeable', String(event.target.checked))}
                    className="accent-[#1E6DFF] w-4 h-4"
                  />
                  <label htmlFor="exchange" className="text-sm text-[#2D323A]">{t('addVehicle.step4.exchangeable')}</label>
                </div>

                <div>
                  <label className="block text-sm text-[#2D323A] mb-1.5 font-medium">{t('addVehicle.step4.financing')}</label>
                  <div className="flex gap-3">
                    {['Yes', 'No', 'Bank only'].map(o => {
                      const key = o === 'Yes' ? 'yes' : o === 'No' ? 'no' : 'bankOnly';
                      const isSelected = (formData.FinancingAvailable || 'No') === o;
                      return (
                        <label key={o} className={`flex items-center gap-2 px-4 py-2 border rounded-xl cursor-pointer hover:border-[#1E6DFF] transition-colors text-sm ${isSelected ? 'border-[#1E6DFF] bg-[#1E6DFF]/5 text-[#1E6DFF]' : 'border-[#E5E7EB]'}`}>
                          <input
                            type="radio"
                            name="financing"
                            checked={isSelected}
                            onChange={() => saveValue('FinancingAvailable', o)}
                            className="accent-[#1E6DFF]"
                          />
                          {t(`addVehicle.step4.financingOptions.${key}`)}
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Contact Preferences */}
          {step === 5 && (
            <div>
              <h2 className="text-xl text-[#2D323A] mb-5">{t('addVehicle.step5.title')}</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-[#F5F7FA] rounded-xl">
                  <input
                    type="checkbox"
                    id="showPhone"
                    checked={formData.ShowPhone !== 'false'}
                    onChange={event => saveValue('ShowPhone', String(event.target.checked))}
                    className="accent-[#1E6DFF] w-4 h-4"
                  />
                  <div>
                    <label htmlFor="showPhone" className="text-sm text-[#2D323A]">{t('addVehicle.step5.showPhone')}</label>
                    <p className="text-xs text-[#717182]">{t('addVehicle.step5.showPhoneDesc')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-[#F5F7FA] rounded-xl">
                  <input
                    type="checkbox"
                    id="showWhatsApp"
                    checked={formData.ShowWhatsApp !== 'false'}
                    onChange={event => saveValue('ShowWhatsApp', String(event.target.checked))}
                    className="accent-[#1E6DFF] w-4 h-4"
                  />
                  <div>
                    <label htmlFor="showWhatsApp" className="text-sm text-[#2D323A]">{t('addVehicle.step5.showWhatsApp')}</label>
                    <p className="text-xs text-[#717182]">{t('addVehicle.step5.showWhatsAppDesc')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-[#F5F7FA] rounded-xl">
                  <input
                    type="checkbox"
                    id="showMsg"
                    checked={formData.ShowMessages !== 'false'}
                    onChange={event => saveValue('ShowMessages', String(event.target.checked))}
                    className="accent-[#1E6DFF] w-4 h-4"
                  />
                  <div>
                    <label htmlFor="showMsg" className="text-sm text-[#2D323A]">{t('addVehicle.step5.showMessages')}</label>
                    <p className="text-xs text-[#717182]">{t('addVehicle.step5.showMessagesDesc')}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-[#2D323A] mb-1.5 font-medium">
                    {t('addVehicle.step5.publicPhone')}
                  </label>
                  <div className={`flex bg-[#F5F7FA] border rounded-xl overflow-hidden transition-colors ${
                    errors.PhoneNumber ? 'border-[#EF4444]' : 'border-[#E5E7EB] focus-within:border-[#1E6DFF]'
                  }`}>
                    <select
                      aria-label="Country calling code"
                      value={countryCode}
                      onChange={event => {
                        const nextCountryCode = event.target.value;
                        setCountryCode(nextCountryCode);
                        saveValue('Public Phone Number', localPhoneNumber ? `${nextCountryCode}${localPhoneNumber}` : '');
                      }}
                      className="min-w-[140px] px-3 py-3 bg-transparent border-r border-[#E5E7EB] outline-none text-sm font-medium"
                    >
                      {COUNTRY_CODES.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.name} ({country.code})
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={localPhoneNumber}
                      maxLength={COUNTRY_CODES.find(country => country.code === countryCode)?.digits
                        || COUNTRY_CODES.find(country => country.code === countryCode)?.maxDigits
                        || 15}
                      onChange={event => {
                        const selectedCountry = COUNTRY_CODES.find(country => country.code === countryCode);
                        const maxLength = selectedCountry?.digits || selectedCountry?.maxDigits || 15;
                        const digits = event.target.value.replace(/\D/g, '').slice(0, maxLength);
                        setLocalPhoneNumber(digits);
                        saveValue('Public Phone Number', digits ? `${countryCode}${digits}` : '');
                      }}
                      placeholder="Enter Mobile Number"
                      className="flex-1 min-w-0 px-4 py-3 bg-transparent outline-none"
                    />
                  </div>
                  {errors.PhoneNumber && <p className="text-xs text-[#EF4444] mt-1">{errors.PhoneNumber}</p>}
                </div>
                <div>
  <label className="block text-sm text-[#2D323A] mb-1.5 font-medium">
    {t('addVehicle.step5.contactHours')}
  </label>

  <div className="grid grid-cols-2 gap-3">
    {/* Start Time */}
    <div>
      <label
        htmlFor="contactHoursStart"
        className="block text-sm text-[#2D323A] mb-1.5 font-medium"
      >
        Start Time
      </label>

      <input
        id="contactHoursStart"
        aria-label="Start Time"
        type="time"
        value={formData.ContactHoursStart || '09:00'}
        onChange={event => {
          const value = event.target.value;

          saveValue('ContactHoursStart', value);

          // Clear contact-hours error when user changes the value
          setErrors(previous => ({
            ...previous,
            ContactHours: '',
          }));
        }}
        className={`w-full px-4 py-3 bg-[#F5F7FA] border rounded-xl focus:outline-none focus:border-[#1E6DFF] ${
          errors.ContactHours
            ? 'border-[#EF4444]'
            : 'border-[#E5E7EB]'
        }`}
      />
    </div>

    {/* End Time */}
    <div>
      <label
        htmlFor="contactHoursEnd"
        className="block text-sm text-[#2D323A] mb-1.5 font-medium"
      >
        End Time
      </label>

      <input
        id="contactHoursEnd"
        aria-label="End Time"
        type="time"
        value={formData.ContactHoursEnd || '18:00'}
        onChange={event => {
          const value = event.target.value;

          saveValue('ContactHoursEnd', value);

          // Clear contact-hours error when user changes the value
          setErrors(previous => ({
            ...previous,
            ContactHours: '',
          }));
        }}
        className={`w-full px-4 py-3 bg-[#F5F7FA] border rounded-xl focus:outline-none focus:border-[#1E6DFF] ${
          errors.ContactHours
            ? 'border-[#EF4444]'
            : 'border-[#E5E7EB]'
        }`}
      />
    </div>
  </div>

  {errors.ContactHours && (
    <p className="text-xs text-[#EF4444] mt-1.5">
      {errors.ContactHours}
    </p>
  )}
</div>
              </div>
            </div>
          )}

          {/* Step 6: Description */}
          {step === 6 && (
            <div>
              <h2 className="text-xl text-[#2D323A] mb-5">Vehicle Description</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[#2D323A] mb-1.5 font-medium">
                    {t('addVehicle.step6.headline')} <span className="text-[#EF4444]">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder={t('addVehicle.step6.headlinePlaceholder')}
                    value={formData.Title || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData(prev => ({ ...prev, Title: val }));
                      setTitleML({ en: val, de: val });
                      setErrors(prev => ({ ...prev, Title: '' }));
                    }}
                    className={`w-full px-4 py-3 bg-[#F5F7FA] border rounded-xl focus:outline-none transition-colors ${
                      errors.Title ? 'border-[#EF4444] focus:border-[#EF4444]' : 'border-[#E5E7EB] focus:border-[#1E6DFF]'
                    }`}
                  />
                  {errors.Title && <p className="text-xs text-[#EF4444] mt-1">{errors.Title}</p>}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm text-[#2D323A] font-medium">
                      {t('addVehicle.step6.description')} <span className="text-[#EF4444]">*</span>
                    </label>
                    <span className="text-xs text-[#717182]">{t('addVehicle.step6.descriptionHint')}</span>
                  </div>
                  <textarea
                    rows={6}
                    placeholder={t('addVehicle.step6.descriptionPlaceholder')}
                    value={formData.VehicleDescription || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData(prev => ({ ...prev, VehicleDescription: val }));
                      setDescriptionML({ en: val, de: val });
                      setErrors(prev => ({ ...prev, VehicleDescription: '' }));
                    }}
                    className={`w-full px-4 py-3 bg-[#F5F7FA] border rounded-xl focus:outline-none transition-colors resize-none ${
                      errors.VehicleDescription ? 'border-[#EF4444] focus:border-[#EF4444]' : 'border-[#E5E7EB] focus:border-[#1E6DFF]'
                    }`}
                  />
                  {errors.VehicleDescription && <p className="text-xs text-[#EF4444] mt-1">{errors.VehicleDescription}</p>}
                </div>

                {/* Service History commented out as requested */}
                {/* <div>
                  <label className="block text-sm text-[#2D323A] mb-1.5">{t('addVehicle.step6.serviceHistory')}</label>
                  <div className="flex gap-3">
                    {['Full service history', 'Partial history', 'No history'].map(o => {
                      const key = o === 'Full service history' ? 'full' : o === 'Partial history' ? 'partial' : 'none';
                      const isChecked = serviceHistoryML[formLang] === o;
                      return (
                        <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="radio"
                            name={`serviceHistory_${formLang}`}
                            checked={isChecked}
                            onChange={() => setServiceHistoryML({ ...serviceHistoryML, [formLang]: o })}
                            className="accent-[#1E6DFF]"
                          />
                          {t(`addVehicle.step6.serviceHistoryOptions.${key}`)}
                        </label>
                      );
                    })}
                  </div>
                </div> */}

                {/* Condition in Step 6 commented out as requested (already selected in Step 1) */}
                {/* <div>
                  <label className="block text-sm text-[#2D323A] mb-1.5">Condition</label>
                  <input
                    type="text"
                    placeholder="Excellent, Very Good, etc."
                    value={conditionML[formLang] || ''}
                    onChange={(e) => setConditionML({ ...conditionML, [formLang]: e.target.value })}
                    className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF]"
                  />
                </div> */}
              </div>
            </div>
          )}

          {/* Step 7: Preview */}
          {step === 7 && (
            <div>
              <h2 className="text-xl text-[#2D323A] mb-5">{t('addVehicle.step7.title')}</h2>
              <div className="bg-[#F5F7FA] rounded-2xl overflow-hidden border border-[#E5E7EB]">
                <div className="bg-[#BFC2C7] h-48 flex items-center justify-center">
                  {photos[0] ? <img src={photos[0]} alt="Selected vehicle" className="h-full w-full object-cover" /> : <Camera className="w-12 h-12 text-white/50" />}
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-[#2D323A]">{titleML['en'] || formData.Title || 'Vehicle listing'}</h3>
                    <span className="text-[#1E6DFF]">{formData.Price ? `${formData.Price} MAD` : 'Price on request'}</span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm text-[#717182] mb-3">
                    <span>📅 {formData.Year || '—'}</span>
                    <span>📍 {formData.City || '—'}</span>
                    <span>⛽ {selectedOptionName(formOptions.fuelTypes, formData.FuelTypeID, 'FuelTypeName')}</span>
                    <span>🔢 {formData.Mileage ? `${formData.Mileage} km` : '—'}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {selectedFeatures.slice(0, 6).map(fId => {
                      const feat = (formOptions.features || []).find((x: any) => x.id === fId);
                      const name = feat?.FeatureName || fId;
                      return (
                        <span key={fId} className="text-xs px-2 py-0.5 bg-white border border-[#E5E7EB] rounded-lg text-[#717182]">{name}</span>
                      );
                    })}
                    {selectedFeatures.length > 6 && <span className="text-xs px-2 py-0.5 bg-white border border-[#E5E7EB] rounded-lg text-[#717182]">+{selectedFeatures.length - 6} more</span>}
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 bg-[#22C55E]/5 border border-[#22C55E]/20 rounded-xl">
                <p className="text-sm text-[#22C55E] flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {isEditMode ? t('addVehicle.step7.reviewChanges') : t('addVehicle.step7.publishReady')}
                </p>
              </div>
              {formError && (
                <div className="mt-4 flex items-center gap-2 rounded-xl border border-[#EF4444]/30 bg-[#EF4444]/5 p-3 text-sm text-[#EF4444]">
                  <AlertCircle className="h-4 w-4 shrink-0" />{formError}
                </div>
              )}
            </div>
          )}

          {/* Step 8: Published */}
          {step === 8 && (
            <div className="text-center py-6">
              <div className="w-20 h-20 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-[#22C55E]" />
              </div>
              <h2 className="text-2xl text-[#2D323A] mb-2">
                {isEditMode ? t('addVehicle.step8.title.edit') : t('addVehicle.step8.title.add')}
              </h2>
              <p className="text-[#717182] mb-6">
                {isEditMode ? t('addVehicle.step8.subtitle.edit') : t('addVehicle.step8.subtitle.add')}
              </p>
              <div className="bg-[#F5F7FA] rounded-xl p-4 mb-6 text-left">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#717182]">{t('addVehicle.step8.listingId')}</span>
                  <span className="text-[#2D323A]">{savedVehicleId ? `#${savedVehicleId}` : '—'}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-[#717182]">{t('addVehicle.step8.status')}</span>
                  <span className="text-[#22C55E] flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {t('addVehicle.step8.active')}</span>
                </div>
              </div>
              <div className="space-y-3">
                <Link to={listingsPath} className="block w-full py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md">
                  {t('addVehicle.step8.buttons.manageListings')}
                </Link>
                <Link to={addVehiclePath} className="block w-full py-3 border border-[#E5E7EB] text-[#2D323A] rounded-xl hover:bg-[#F5F7FA] transition-colors">
                  {t('addVehicle.step8.buttons.addAnother')}
                </Link>
              </div>
            </div>
          )}

          {step < 8 && (
            <div className="flex gap-3 mt-8">
              {step > 0 && (
                <button onClick={prev} className="px-6 py-3 border border-[#E5E7EB] text-[#2D323A] rounded-xl hover:bg-[#F5F7FA] transition-colors">
                  {t('addVehicle.buttons.back')}
                </button>
              )}
              <button onClick={next} className="flex-1 py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md flex items-center justify-center gap-2">
                {step === 7 ? (isEditMode ? t('addVehicle.buttons.saveChanges') : t('addVehicle.buttons.publish')) : t('addVehicle.buttons.continue')}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Keep FEATURES_LIST as original English strings for backend mapping
const FEATURES_LIST = [
  'Leather Seats', 'Heated Seats', 'Ventilated Seats', 'Memory Seats',
  'Panoramic Sunroof', 'Sunroof', 'Navigation GPS', 'Apple CarPlay',
  'Android Auto', 'Bluetooth', 'USB Ports', 'Wireless Charging',
  'Reverse Camera', '360° Camera', 'Parking Sensors', 'Parking Assist',
  'Adaptive Cruise Control', 'Lane Departure Warning', 'Blind Spot Monitor',
  'Automatic Emergency Braking', 'LED Headlights', 'Keyless Entry',
  'Push Start', 'Remote Start', 'Climate Control', 'Dual Zone Climate',
  'Alloy Wheels', 'Tinted Windows', 'Electric Trunk', 'Head-up Display',
];
